import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import Topbar from "../components/Topbar";
import { Sparkles, Send, Loader2, Globe, FolderKanban } from "lucide-react";

const MODE_PROJECT = "project";
const MODE_FEEDS   = "feeds";

export default function AIAssistant() {
  const { currentWorkspaceId } = useWorkspace();
  const [search] = useSearchParams();
  const initialProject = search.get("project");
  const initialMode = search.get("mode") === MODE_FEEDS ? MODE_FEEDS : MODE_PROJECT;
  const [mode, setMode] = useState(initialMode);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initialProject || "");
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    api.get("/projects", { params: { workspace_id: currentWorkspaceId } }).then((r) => {
      setProjects(r.data);
      if (!projectId && r.data[0]) setProjectId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  useEffect(() => {
    setHistory([]);
    sessionRef.current = null;
  }, [projectId, mode, currentWorkspaceId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  const ask = async () => {
    if (!q.trim()) return;
    if (mode === MODE_PROJECT && !projectId) return;
    const question = q;
    setHistory([...history, { role: "user", text: question }]);
    setQ("");
    setLoading(true);
    try {
      let r;
      if (mode === MODE_FEEDS) {
        r = await api.post("/ai/workspace-assistant", {
          workspace_id: currentWorkspaceId,
          question,
          session_id: sessionRef.current,
        });
      } else {
        r = await api.post("/ai/project-assistant", {
          project_id: projectId, question, session_id: sessionRef.current,
        });
      }
      sessionRef.current = r.data.session_id;
      setHistory((h) => [...h, { role: "assistant", text: r.data.answer }]);
    } catch (e) {
      setHistory((h) => [...h, { role: "assistant", text: e?.response?.data?.detail || "Error reaching assistant." }]);
    } finally {
      setLoading(false);
    }
  };

  const SUGGESTIONS = mode === MODE_FEEDS ? [
    "What's new on our website this week?",
    "Summarise the latest posts across our social channels.",
    "Which of our social profiles failed to sync today?",
    "Has anything important changed on our website lately?",
  ] : [
    "What's the critical path for the next 30 days?",
    "Summarise overdue tasks and propose owners.",
    "List risks blocking the soft launch milestone.",
    "Draft a stand-up agenda for tomorrow.",
  ];

  const activeProj = projects.find((p) => p.id === projectId);
  const title = mode === MODE_FEEDS ? "Ask about your hotel website & social feeds" : `Ask about ${activeProj?.name || "your project"}`;
  const blurb = mode === MODE_FEEDS
    ? "Grounded live in your Hotel Website daily snapshot + Hotel Social hourly posts. The assistant only answers from what's been scraped — no hallucinations."
    : "The assistant has live context: tasklists, open tasks, recent comments. Ask in any language.";

  return (
    <div className="flex flex-col h-screen" data-testid="ai-assistant-page">
      <Topbar
        title="AI Assistant"
        subtitle={mode === MODE_FEEDS ? "Grounded in your hotel feeds" : "Powered by GPT-5.2 — ask anything about your projects"}
        breadcrumb="Intelligence"
        right={
          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div className="inline-flex items-center bg-[#141417] border border-zinc-800 rounded-md p-0.5" data-testid="ai-mode-toggle">
              <button
                type="button"
                onClick={() => setMode(MODE_PROJECT)}
                data-testid="ai-mode-project"
                className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded inline-flex items-center gap-1.5 transition-colors ${
                  mode === MODE_PROJECT ? "bg-[#D4A968] text-black" : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                <FolderKanban className="w-3 h-3" />
                Project
              </button>
              <button
                type="button"
                onClick={() => setMode(MODE_FEEDS)}
                data-testid="ai-mode-feeds"
                className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded inline-flex items-center gap-1.5 transition-colors ${
                  mode === MODE_FEEDS ? "bg-[#D4A968] text-black" : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                <Globe className="w-3 h-3" />
                Hotel Feeds
              </button>
            </div>
            {mode === MODE_PROJECT && (
              <select
                value={projectId} onChange={(e) => setProjectId(e.target.value)}
                data-testid="ai-project-select"
                className="bg-[#141417] border border-zinc-800 hover:border-zinc-700 rounded-md px-2.5 py-2 text-xs text-white outline-none"
              >
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        }
      />
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full p-6 overflow-hidden">
        {history.length === 0 && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="ai-empty-state">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D4A968] to-[#7C5E2C] flex items-center justify-center mb-4">
              {mode === MODE_FEEDS ? <Globe className="w-6 h-6 text-black" /> : <Sparkles className="w-6 h-6 text-black" />}
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">{title}</h2>
            <p className="text-sm text-zinc-500 max-w-md mb-6">{blurb}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl w-full">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setQ(s)}
                  data-testid={`suggestion-${s.slice(0, 10)}`}
                  className="text-left bg-[#141417] border border-zinc-800 hover:border-[#D4A968] rounded-md px-3 py-2 text-xs text-zinc-300 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {(history.length > 0 || loading) && (
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="ai-conversation">
            {history.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-2xl rounded-lg px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-[#D4A968] text-black" : "bg-[#141417] border border-zinc-800 text-zinc-100"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#141417] border border-zinc-800 rounded-lg px-4 py-3 flex items-center gap-2 text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin text-[#D4A968]" />
                  <span className="text-sm">GPT-5.2 is thinking…</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 border-t border-zinc-800 pt-3" data-testid="ai-input-bar">
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder={mode === MODE_FEEDS ? "Ask about your website or social posts…" : `Ask about ${activeProj?.name || "the project"}…`}
            data-testid="ai-input"
            className="flex-1 bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md px-3 py-3 text-sm text-white outline-none"
          />
          <button
            onClick={ask} disabled={loading || !q.trim() || (mode === MODE_PROJECT && !projectId)}
            data-testid="ai-send"
            className="bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold px-4 py-3 rounded-md text-sm flex items-center gap-1.5 disabled:opacity-40"
          >
            <Send className="w-3.5 h-3.5" />
            {loading ? "…" : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}
